import { useEffect, useRef } from 'react';

import { setupProjectPreview } from '@/store/slices/project';
import { PublishProjectPreview } from '@/connection/projects/publish-project-preview';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectProjectPreview, selectSettingUpPreview } from '@/store/slices/project/selectors';
import { BrandliveEvent } from '@/types/projects.types';

interface IAutoCreateAndPublishPreviewProps {
  workingProject: BrandliveEvent | null;
  skipHook?: boolean;
}

const useAutoCreateAndPublishPreview = ({ workingProject, skipHook }: IAutoCreateAndPublishPreviewProps) => {
  const dispatch = useAppDispatch();

  const settingUpPreviewRef = useRef(false);

  const settingUpPreview = useAppSelector(selectSettingUpPreview);
  const eventPreviewBySelector = useAppSelector(selectProjectPreview);

  const projectUuid = workingProject?.uuid;
  const projectChannel = workingProject?.channel;
  const eventPreview = workingProject?.event_preview || eventPreviewBySelector;

  // this hook automatically publishes a preview of the project
  useEffect(() => {
    if (skipHook) return;
    if (!workingProject) return;
    if (!projectUuid || !projectChannel || settingUpPreview) return;
    if (settingUpPreviewRef.current) return;
    // If project does not have a "published preview"
    if (!eventPreview) {
      /* use the project channel not the user active channel because we have "global" projects too */
      (async () => {
        try {
          settingUpPreviewRef.current = true;
          await dispatch(setupProjectPreview({ eventUuid: projectUuid, channel: projectChannel }));
        } catch (error) {
          console.error('Error setting up preview', error);
        } finally {
          settingUpPreviewRef.current = false;
        }
      })();
    } else {
      // TODO
      // what if a user updates the template? How should we re-publish without needing
      // to call this republish every single time this page is rendered? Or, should we just
      // republish every time this page is rendered?
      (async () => {
        try {
          // no need to await
          PublishProjectPreview(eventPreview.uuid, projectUuid, eventPreview.url, workingProject.channel);
        } catch (error) {
          console.error('Error publishing preview', error);
        }
      })();
    }
  }, [dispatch, eventPreview, settingUpPreview, projectChannel, projectUuid, workingProject, skipHook]);
};

export default useAutoCreateAndPublishPreview;
